/**
 * Standard directive library — ports the seven shipped with
 * `@json-render/directives` plus a token-friendly `$t` placeholder
 * that uses an injected translations dict.
 *
 * Every directive is pure: it reads from the resolution context (state
 * + functions) but never mutates. All return undefined gracefully when
 * inputs are missing, so a not-yet-loaded `$state` reference doesn't
 * crash the render.
 */

import { defineDirective, type DirectiveDefinition } from "./directives"

import { type ResolveContext, resolvePropValue } from "./expressions"

// Helper: resolve a directive sub-value via the main resolver so
// `$state`, `$computed`, etc. nest naturally inside directive args.
function r(value: unknown, ctx: ResolveContext): unknown {
  return resolvePropValue(value, ctx)
}

function asNumber(value: unknown): number {
  if (typeof value === "number") return value
  if (typeof value === "string") {
    const n = Number(value)
    return Number.isFinite(n) ? n : NaN
  }
  return NaN
}

// ── $format ─────────────────────────────────────────────────────────

export const formatDirective = defineDirective({
  name: "$format",
  description:
    'Locale-aware value formatting. Modes: "date" | "currency" | "number" | "percent". Uses Intl.NumberFormat / DateTimeFormat. Date supports style:"relative" for relative-time strings.',
  resolve(value, ctx) {
    const mode = String(value.$format)
    const raw = r(value.value, ctx)
    const locale = typeof value.locale === "string" ? value.locale : undefined
    const options =
      typeof value.options === "object" && value.options !== null
        ? (value.options as Record<string, unknown>)
        : {}

    switch (mode) {
      case "date": {
        const date = raw instanceof Date ? raw : new Date(raw as string | number)
        if (Number.isNaN(date.getTime())) return undefined
        if (value.style === "relative") {
          const now = typeof value.now === "number" ? value.now : Date.now()
          const diff = now - date.getTime()
          if (diff === 0) return "just now"
          const abs = Math.abs(diff)
          const suffix = diff > 0 ? "ago" : "from now"
          const sec = Math.floor(abs / 1000)
          const min = Math.floor(sec / 60)
          const hr = Math.floor(min / 60)
          const day = Math.floor(hr / 24)
          if (day > 0) return `${day}d ${suffix}`
          if (hr > 0) return `${hr}h ${suffix}`
          if (min > 0) return `${min}m ${suffix}`
          return `${sec}s ${suffix}`
        }
        return new Intl.DateTimeFormat(locale, options as Intl.DateTimeFormatOptions).format(date)
      }
      case "currency": {
        const num = asNumber(raw)
        if (Number.isNaN(num)) return undefined
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: typeof value.currency === "string" ? value.currency : "USD",
          ...(options as Intl.NumberFormatOptions),
        }).format(num)
      }
      case "number": {
        const num = asNumber(raw)
        if (Number.isNaN(num)) return undefined
        const notation =
          typeof value.notation === "string"
            ? (value.notation as Intl.NumberFormatOptions["notation"])
            : undefined
        return new Intl.NumberFormat(locale, {
          ...(notation ? { notation } : {}),
          ...(options as Intl.NumberFormatOptions),
        }).format(num)
      }
      case "percent": {
        const num = asNumber(raw)
        if (Number.isNaN(num)) return undefined
        return new Intl.NumberFormat(locale, {
          style: "percent",
          ...(options as Intl.NumberFormatOptions),
        }).format(num)
      }
      default:
        return raw
    }
  },
})

// ── $math ───────────────────────────────────────────────────────────

export const mathDirective = defineDirective({
  name: "$math",
  description:
    'Numeric computation over expression args. Modes: "add" | "sub" | "mul" | "div" | "mod" | "abs" | "min" | "max" | "round" | "floor" | "ceil". Args nest expressions naturally.',
  resolve(value, ctx) {
    const mode = String(value.$math)
    const args = Array.isArray(value.args) ? value.args.map((a) => asNumber(r(a, ctx))) : []

    switch (mode) {
      case "add":
        return args.reduce((a, b) => a + b, 0)
      case "sub":
        return args.length === 0 ? 0 : args.reduce((a, b, i) => (i === 0 ? b : a - b), 0)
      case "mul":
        return args.reduce((a, b) => a * b, 1)
      case "div":
        if (args.length < 2) return undefined
        return args.slice(1).reduce((a, b) => (b === 0 ? NaN : a / b), args[0]!)
      case "mod":
        if (args.length !== 2) return undefined
        return args[1] === 0 ? NaN : args[0]! % args[1]!
      case "abs":
        return args.length === 1 ? Math.abs(args[0]!) : undefined
      case "min":
        return args.length === 0 ? undefined : Math.min(...args)
      case "max":
        return args.length === 0 ? undefined : Math.max(...args)
      case "round":
        return args.length === 1 ? Math.round(args[0]!) : undefined
      case "floor":
        return args.length === 1 ? Math.floor(args[0]!) : undefined
      case "ceil":
        return args.length === 1 ? Math.ceil(args[0]!) : undefined
      default:
        return undefined
    }
  },
})

// ── $concat ─────────────────────────────────────────────────────────

export const concatDirective = defineDirective({
  name: "$concat",
  description:
    'String concatenation. `{$concat: ["Hello, ", {$state:"/name"}, "!"]}`. Coerces each piece to string; null/undefined become empty.',
  resolve(value, ctx) {
    const parts = Array.isArray(value.$concat) ? value.$concat : []
    return parts
      .map((p) => {
        const resolved = r(p, ctx)
        if (resolved === null || resolved === undefined) return ""
        return String(resolved)
      })
      .join("")
  },
})

// ── $count ──────────────────────────────────────────────────────────

export const countDirective = defineDirective({
  name: "$count",
  description:
    'Count items in an array. `{$count: {$state: "/orders"}}` returns orders.length, or 0 when not an array.',
  resolve(value, ctx) {
    const source = r(value.$count, ctx)
    return Array.isArray(source) ? source.length : 0
  },
})

// ── $truncate ───────────────────────────────────────────────────────

export const truncateDirective = defineDirective({
  name: "$truncate",
  description:
    'Trim a string to a max length, appending an ellipsis. `{$truncate: {$state:"/title"}, max: 50, suffix: "…"}`.',
  resolve(value, ctx) {
    const str = r(value.$truncate, ctx)
    if (typeof str !== "string") return str
    const max = typeof value.max === "number" ? value.max : 80
    const suffix = typeof value.suffix === "string" ? value.suffix : "…"
    if (str.length <= max) return str
    if (max <= suffix.length) return suffix.slice(0, max)
    return str.slice(0, max - suffix.length) + suffix
  },
})

// ── $pluralize ──────────────────────────────────────────────────────

export const pluralizeDirective = defineDirective({
  name: "$pluralize",
  description:
    'Pluralisation by count. `{$pluralize: {$state:"/n"}, one: "item", other: "items", zero?: "no items"}`. `zero` is optional — falls back to `other`.',
  resolve(value, ctx) {
    const n = asNumber(r(value.$pluralize, ctx))
    if (Number.isNaN(n)) return undefined
    if (n === 0 && typeof value.zero === "string") return value.zero
    if (n === 1 && typeof value.one === "string") return value.one
    return typeof value.other === "string" ? value.other : undefined
  },
})

// ── $join ───────────────────────────────────────────────────────────

export const joinDirective = defineDirective({
  name: "$join",
  description:
    'Array join with separator. `{$join: {$state: "/tags"}, sep: ", "}`. Coerces each item to string.',
  resolve(value, ctx) {
    const arr = r(value.$join, ctx)
    if (!Array.isArray(arr)) return ""
    const sep = typeof value.sep === "string" ? value.sep : ", "
    return arr
      .map((item) => {
        if (item === null || item === undefined) return ""
        return String(item)
      })
      .join(sep)
  },
})

// ── $t ──────────────────────────────────────────────────────────────

/**
 * i18n directive factory. Pass a `translations` map keyed by locale
 * key, return the configured directive. Locale resolution is up to
 * the host — typically wired via `react-i18next` resolved key paths.
 *
 * Usage in a spec:
 *   {$t: "dash.tile.export", count: {$state: "/n"}}
 *
 * Usage from app code:
 *   const directives = [...standardDirectives, createTDirective({get: i18n.t})]
 */
export interface TDirectiveOptions {
  /** Resolve a translation key to a template string. */
  get: (key: string) => string | undefined
  /**
   * Replace `{{name}}` placeholders. Defaults to a simple regex
   * substitution of stringified values.
   */
  interpolate?: (template: string, params: Record<string, unknown>) => string
}

export function createTDirective(options: TDirectiveOptions): DirectiveDefinition {
  const interpolate =
    options.interpolate ??
    ((template, params) =>
      template.replace(/\{\{([^}]+)\}\}/g, (_, name: string) => {
        const v = params[name.trim()]
        return v === undefined || v === null ? "" : String(v)
      }))

  return defineDirective({
    name: "$t",
    description:
      'Translate a key. `{$t: "dash.tile.export"}` or with interpolation `{$t: "dash.greeting", name: {$state:"/user"}}`. Keys come from the host i18n config.',
    resolve(value, ctx) {
      const key = String(value.$t)
      const template = options.get(key)
      if (template === undefined) return key // missing key — show the key itself

      const params: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(value)) {
        if (k === "$t") continue
        params[k] = r(v, ctx)
      }
      return interpolate(template, params)
    },
  })
}

// ── Standard set ────────────────────────────────────────────────────

/**
 * All non-factory directives in one array. Spread with factory
 * directives (like `createTDirective`) when registering.
 */
export const standardDirectives: DirectiveDefinition[] = [
  formatDirective,
  mathDirective,
  concatDirective,
  countDirective,
  truncateDirective,
  pluralizeDirective,
  joinDirective,
]
